import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { navigationStateFrom } from '@/types/navigation';
import { Grid01, LayoutGrid01, LayoutRight } from '@untitledui/icons';
import { Plus, SearchLg, Package } from '@untitledui/icons';
import { getLocalTimeZone, today, startOfMonth, endOfMonth, parseDate } from '@internationalized/date';
import type { DateValue } from 'react-aria-components';
import { useListData } from 'react-stately';

// RangeValue type for date ranges
type RangeValue<T> = { start: T; end: T } | null;
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { MultiSelect } from '@/components/base/select/multi-select';
import type { Container, ContainerStatus, RiskLevel, DocumentStatus } from '@/types/container';
import { useTranslation } from 'react-i18next';
import { useI18n } from '@/hooks/use-i18n';
import { ContainerSpreadsheetView } from '@/components/application/containers/container-spreadsheet-view';
import { DistributionChartsSection } from '@/components/application/containers/distribution-charts-section';
import { RecentContainersTable } from '@/components/application/containers/recent-containers-table';
import { Button } from '@/components/base/buttons/button';
import { DateRangePicker } from '@/components/application/date-picker/date-range-picker';
import {
    useContainerStatsRecap,
    useContainerTable,
    useCarriersInUse,
    useRefetchContainersRecap,
} from '@/hooks/api/use-containers-recap';
import { SHIPMENT_STATUS_OPTIONS } from '@/types/api/containers-recap';
import type { RiskLevel as ApiRiskLevel, ShipmentStatusFilter } from '@/types/api/containers-recap';
import { Toggle } from '@/components/base/toggle/toggle';
import { parseUtcISO } from '@/utils/date-formatters';
import { storage } from '@/lib/local-storage';
import { ShipperOnboardingHeaderAction } from '@/components/application/onboarding/shipper-onboarding-context';

const SHIPPER_CONTAINERS_VIEW_STORAGE_KEY = 'shipper.containers.viewMode';

// Default status filter: all except draft (booked are valid shipments)
const DEFAULT_STATUS_IDS: ShipmentStatusFilter[] = ['booked', 'in_transit', 'arrived', 'delivered', 'on_hold', 'cancelled'];

type ViewMode = 'recap' | 'spreadsheet';

// Helper to format DateValue to ISO string
const formatDateToISO = (d: DateValue): string => {
    return `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;
};

// Helper to get date range strings from DateValue range
const getDateRangeStrings = (range: RangeValue<DateValue> | null): { startDate: string | null; endDate: string | null } => {
    if (!range || !range.start || !range.end) {
        return { startDate: null, endDate: null };
    }
    return {
        startDate: formatDateToISO(range.start),
        endDate: formatDateToISO(range.end),
    };
};

// Helper to parse ISO string to DateValue
const parseISOToDateValue = (isoString: string | null): DateValue | null => {
    if (!isoString) return null;
    try {
        return parseDate(isoString);
    } catch {
        return null;
    }
};

export const ShipperContainersPage = () => {
    const { t } = useTranslation(['pages:containers', 'common']);
    const { translateContainerStatus, translateDocumentStatus, translateRiskLevel } = useI18n();
    const [containers, setContainers] = useState<Container[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    // Risk level multi-select state using useListData
    const selectedRiskLevels = useListData<{ id: string; label: string }>({
        initialItems: [],
    });
    const [hasDocIssuesFilter, setHasDocIssuesFilter] = useState(false);
    const [hasActiveAlertsFilter, setHasActiveAlertsFilter] = useState(false);
    const [hasPartialDataFilter, setHasPartialDataFilter] = useState(false);
    const [carrierNameFilter, setCarrierNameFilter] = useState<string>('');
    const [sortBy, setSortBy] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [showFiltersPanel, setShowFiltersPanel] = useState(false);
    const [showRecapSidebar, setShowRecapSidebar] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    // Date range state - default to this month
    const now = today(getLocalTimeZone());
    const [dateRange, setDateRange] = useState<RangeValue<DateValue> | null>(() => {
        // Initialize from URL params if present
        const startParam = searchParams.get('start_date');
        const endParam = searchParams.get('end_date');
        if (startParam && endParam) {
            const start = parseISOToDateValue(startParam);
            const end = parseISOToDateValue(endParam);
            if (start && end) {
                return { start, end };
            }
        }
        // Default to this month
        return {
            start: startOfMonth(now),
            end: endOfMonth(now),
        };
    });

    const getValidViewMode = (view: string | null): ViewMode => {
        const validModes: ViewMode[] = ['recap', 'spreadsheet'];
        return (validModes.includes(view as ViewMode) ? view : 'recap') as ViewMode;
    };
    const [viewMode, setViewMode] = useState<ViewMode>(() => {
        const fromUrl = searchParams.get('view');
        if (fromUrl) return getValidViewMode(fromUrl);
        return getValidViewMode(storage.getItem(SHIPPER_CONTAINERS_VIEW_STORAGE_KEY));
    });

    useEffect(() => {
        storage.setItem(SHIPPER_CONTAINERS_VIEW_STORAGE_KEY, viewMode);
    }, [viewMode]);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(30);

    // Ref to track if we're initializing from URL (prevents sync loop)
    const isInitializing = useRef(true);

    // Risk level options for multi-select (defined early so it can be used in initialization)
    const riskLevelOptions = useMemo(() => [
        { id: 'low', label: t('riskLevel.low', { ns: 'types' }) },
        { id: 'medium', label: t('riskLevel.medium', { ns: 'types' }) },
        { id: 'high', label: t('riskLevel.high', { ns: 'types' }) },
        { id: 'critical', label: t('riskLevel.critical', { ns: 'types' }) }
    ], [t]);

    // Shipment status options (skip "All Statuses" for multi-select)
    const statusOptions = useMemo(() => SHIPMENT_STATUS_OPTIONS
        .filter((opt) => opt.id !== '')
        .map((opt) => ({
            id: opt.id,
            label: t(opt.labelKey, { defaultValue: opt.label }),
        })), [t]);

    const defaultStatusOptions = useMemo(() =>
        statusOptions.filter((o) => DEFAULT_STATUS_IDS.includes(o.id as ShipmentStatusFilter)),
        [statusOptions]);

    // Status multi-select: start empty, populated once in effect (single source of truth to avoid duplicates)
    const selectedStatusFilters = useListData<{ id: string; label: string }>({
        initialItems: [],
    });

    // Track if URL params have been initialized (prevents double-init in StrictMode)
    const urlParamsInitialized = useRef(false);

    // Initialize filters from URL params on mount
    useEffect(() => {
        // Prevent double initialization (React StrictMode runs effects twice)
        if (urlParamsInitialized.current) return;
        urlParamsInitialized.current = true;

        const statusParam = searchParams.get('status');
        const risk = searchParams.get('risk');
        const hasDocIssues = searchParams.get('has_doc_issues');
        const hasActiveAlerts = searchParams.get('has_active_alerts');
        const carrierName = searchParams.get('carrier_name');
        const sort = searchParams.get('sort_by');
        const order = searchParams.get('sort_order');
        const page = searchParams.get('page');
        const pageSizeParam = searchParams.get('page_size');

        // Status: single source of truth — either from URL or default (no initialItems to avoid duplicates)
        if (statusParam) {
            const ids = [...new Set(statusParam.split(',').filter(Boolean))];
            const toSet = ids
                .map((id) => statusOptions.find((o) => o.id === id))
                .filter((o): o is { id: ShipmentStatusFilter; label: string } => !!o && o.id !== '');
            // Deduplicate by id (same id can appear once)
            const seenIds = new Set<string>();
            const toSetUnique = toSet.filter((o) => {
                if (seenIds.has(o.id)) return false;
                seenIds.add(o.id);
                return true;
            });
            for (let i = selectedStatusFilters.items.length - 1; i >= 0; i--) {
                selectedStatusFilters.remove(i);
            }
            toSetUnique.forEach((opt) => selectedStatusFilters.append(opt));
        } else {
            defaultStatusOptions.forEach((opt) => {
                if (!selectedStatusFilters.getItem(opt.id)) {
                    selectedStatusFilters.append(opt);
                }
            });
        }
        if (risk) {
            // Deduplicate risk IDs from URL using Set
            const uniqueRiskIds = [...new Set(risk.split(',').filter(Boolean))];
            // Add each unique risk level (clear first to avoid duplicates)
            uniqueRiskIds.forEach(id => {
                const option = riskLevelOptions.find(o => o.id === id);
                if (option && !selectedRiskLevels.getItem(id)) {
                    selectedRiskLevels.append(option);
                }
            });
        }
        if (hasDocIssues === 'true') setHasDocIssuesFilter(true);
        if (hasActiveAlerts === 'true') setHasActiveAlertsFilter(true);
        if (searchParams.get('has_partial_data') === 'true') setHasPartialDataFilter(true);
        if (carrierName) setCarrierNameFilter(carrierName);
        if (sort) setSortBy(sort);
        if (order === 'asc' || order === 'desc') setSortOrder(order);
        if (page) setCurrentPage(parseInt(page, 10) || 1);
        if (pageSizeParam) {
            const parsedPageSize = parseInt(pageSizeParam, 10);
            if ([10, 20, 30, 50, 100].includes(parsedPageSize)) {
                setPageSize(parsedPageSize);
            }
        }

        // Mark initialization complete after a tick
        setTimeout(() => {
            isInitializing.current = false;
        }, 0);
    }, []); // Only run once on mount

    // Convert date range to API format
    const { startDate, endDate } = useMemo(() => getDateRangeStrings(dateRange), [dateRange]);

    // Get risk level filter array from selectedRiskLevels (deduplicated)
    const riskLevelFilters = useMemo(() => {
        const ids = selectedRiskLevels.items.map(item => item.id);
        return [...new Set(ids)]; // Ensure no duplicates
    }, [selectedRiskLevels.items]);

    // Status filter array from multi-select (for API and URL), deduplicated by id
    const statusFilters = useMemo(() => {
        const ids = selectedStatusFilters.items.map((i) => i.id);
        return [...new Set(ids)] as ShipmentStatusFilter[];
    }, [selectedStatusFilters.items]);

    // Status selection differs from default (draft/booked excluded)
    const hasNonDefaultStatusFilter = useMemo(() => {
        const ids = [...statusFilters].sort();
        const def = [...DEFAULT_STATUS_IDS].sort();
        return ids.length !== def.length || ids.some((id, i) => id !== def[i]);
    }, [statusFilters]);

    // API hooks for real data
    const {
        data: statsData,
        isLoading: _statsLoading,
    } = useContainerStatsRecap({
        startDate,
        endDate,
        riskLevels: riskLevelFilters.length > 0 ? riskLevelFilters.map((r: string) => r.toLowerCase() as ApiRiskLevel) : undefined,
        shipmentStatus: statusFilters.length > 0 ? statusFilters : undefined,
        carrierName: carrierNameFilter || undefined,
        hasDocIssues: hasDocIssuesFilter || undefined,
        hasActiveAlerts: hasActiveAlertsFilter || undefined,
        hasPartialData: hasPartialDataFilter || undefined,
        enabled: viewMode === 'recap',
    });

    // Fetch carriers actually used in containers
    const { data: carriersInUseData } = useCarriersInUse(startDate, endDate);

    const {
        data: tableData,
        isLoading: tableLoading,
    } = useContainerTable({
        startDate,
        endDate,
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        search: searchTerm || undefined,
        riskLevels: riskLevelFilters.length > 0 ? riskLevelFilters.map((r: string) => r.toLowerCase() as ApiRiskLevel) : undefined,
        shipmentStatus: statusFilters.length > 0 ? statusFilters : undefined,
        carrierName: carrierNameFilter || undefined,
        hasDocIssues: hasDocIssuesFilter || undefined,
        hasActiveAlerts: hasActiveAlertsFilter || undefined,
        hasPartialData: hasPartialDataFilter || undefined,
        sortBy: sortBy || undefined,
        sortOrder,
        enabled: true,
    });

    // Refetch hook for manual data refresh
    const refetchAllData = useRefetchContainersRecap();

    // Sync filters to URL params (debounced to prevent rapid updates)
    const syncFiltersToUrl = useCallback(() => {
        // Don't sync during initialization
        if (isInitializing.current) return;

        const params = new URLSearchParams();

        // View mode
        if (viewMode !== 'recap') params.set('view', viewMode);

        // Date range
        if (startDate) params.set('start_date', startDate);
        if (endDate) params.set('end_date', endDate);

        // Filters
        if (statusFilters.length > 0) params.set('status', statusFilters.join(','));
        if (riskLevelFilters.length > 0) params.set('risk', riskLevelFilters.join(','));
        if (hasDocIssuesFilter) params.set('has_doc_issues', 'true');
        if (hasActiveAlertsFilter) params.set('has_active_alerts', 'true');
        if (hasPartialDataFilter) params.set('has_partial_data', 'true');
        if (carrierNameFilter) params.set('carrier_name', carrierNameFilter);
        if (sortBy) params.set('sort_by', sortBy);
        if (sortOrder !== 'asc') params.set('sort_order', sortOrder);
        if (currentPage > 1) params.set('page', String(currentPage));
        if (pageSize !== 30) params.set('page_size', String(pageSize));

        setSearchParams(params, { replace: true });
    }, [viewMode, startDate, endDate, statusFilters, riskLevelFilters, hasDocIssuesFilter, hasActiveAlertsFilter, hasPartialDataFilter, carrierNameFilter, sortBy, sortOrder, currentPage, pageSize, setSearchParams]);

    // Sync filters to URL when they change (with debounce)
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            syncFiltersToUrl();
        }, 100);
        return () => clearTimeout(timeoutId);
    }, [syncFiltersToUrl]);

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilters, selectedRiskLevels.items, hasDocIssuesFilter, hasActiveAlertsFilter, hasPartialDataFilter, carrierNameFilter]);

    // Handle sort change from table
    const handleSortChange = useCallback((newSortBy: string, newSortOrder: 'asc' | 'desc') => {
        setSortBy(newSortBy);
        setSortOrder(newSortOrder);
        setCurrentPage(1); // Reset to first page on sort change
    }, []);

    // All containers from API (pagination handled by API)
    const displayedContainers = containers;

    // Pagination calculations
    const totalCount = tableData?.total_count || containers.length;
    const totalPages = Math.ceil(totalCount / pageSize);

    // Carrier options from actual container data
    const carrierOptions = useMemo(() => {
        const options = [{ id: '', label: t('list.filters.allCarriers', { defaultValue: 'All Carriers' }) }];
        if (carriersInUseData?.carriers) {
            carriersInUseData.carriers.forEach(carrier => {
                options.push({ id: carrier.carrier_name, label: `${carrier.carrier_name} (${carrier.count})` });
            });
        }
        return options;
    }, [carriersInUseData, t]);

    // Transform API table data to Container format for display
    const apiContainers: Container[] = useMemo(() => {
        if (!tableData?.containers) return [];

        return tableData.containers
            .map((row): Container => {
                // Map API status to frontend ContainerStatus
                const mapStatus = (apiStatus: string): ContainerStatus => {
                    const s = apiStatus?.toLowerCase() || '';
                    const statusMap: Record<string, ContainerStatus> = {
                        'draft': 'Draft',
                        'booked': 'Booked',
                        'in_transit': 'In Transit',
                        'arrived': 'Arrived',
                        'delivered': 'Delivered',
                        'on_hold': 'On Hold',
                        'cancelled': 'Cancelled',
                        // Legacy fallback
                        'booking_confirmed': 'Booked',
                        'documents_pending': 'Booked',
                        'ready_to_ship': 'Booked',
                        'arrived_at_port': 'Arrived',
                        'customs_clearance': 'Arrived',
                        'customs_cleared': 'Arrived',
                        'out_for_delivery': 'Delivered',
                        'completed': 'Delivered',
                    };
                    return statusMap[s] || 'Draft';
                };

                // Map API risk level to frontend RiskLevel
                const mapRiskLevel = (apiRisk: string): RiskLevel => {
                    const r = apiRisk?.toLowerCase() || '';
                    if (r === 'low') return 'Low';
                    if (r === 'medium') return 'Medium';
                    if (r === 'high') return 'High';
                    if (r === 'critical') return 'Critical';
                    return 'Low';
                };

                // Map document status
                const mapDocStatus = (): DocumentStatus => {
                    if (row.documents_complete && row.documents_validated) return 'Completo';
                    if (row.documents_complete && !row.documents_validated) return 'Error';
                    return 'Pending';
                };

                return {
                    id: row.container_id,
                    shipperId: '', // Not available from API
                    receiverId: row.consignee_company_id || '',
                    containerNumber: row.container_number || row.booking_number || undefined,
                    route: {
                        from: row.origin_port_name
                            ? `${row.origin_port_name}${row.origin_country_code ? ` (${row.origin_country_code})` : ''}`
                            : '-',
                        to: row.destination_port_name
                            ? `${row.destination_port_name}${row.destination_country_code ? ` (${row.destination_country_code})` : ''}`
                            : '-',
                    },
                    progress: Math.min(100, Math.max(0, Number(row.progress_percentage) || 0)),
                    eta: row.estimated_arrival_date ? new Date(row.estimated_arrival_date) : undefined as any,
                    originalEta: row.original_estimated_arrival_date ? new Date(row.original_estimated_arrival_date) : undefined,
                    delayDays: row.delay_days ?? null,
                    documentStatus: mapDocStatus(),
                    riskLevel: mapRiskLevel(row.risk_level),
                    value: {
                        amount: row.total_value_usd || row.freight_charges_usd || 0,
                        currency: row.currency || 'USD'
                    },
                    status: mapStatus(row.status),
                    shippingCompany: row.carrier_name || undefined,
                    commodity: 'avocado', // Default, not available from API
                    createdAt: row.created_at ? new Date(row.created_at) : new Date(),
                    updatedAt: row.updated_at ? new Date(row.updated_at) : new Date(),

                    // Extended fields for detailed view
                    bookingNumber: row.booking_number || undefined,
                    blNumber: row.bl_number || undefined,
                    vesselName: row.vessel_name || undefined,
                    voyageNumber: row.voyage_number || undefined,
                    incoterms: row.incoterms || undefined,
                    cargoType: row.cargo_type || undefined,
                    transportMode: row.transport_mode || undefined,
                    purchaseOrderNumber: row.purchase_order_number || undefined,
                    serviceContractNumber: row.service_contract_number || undefined,

                    // Cargo details
                    isHazardous: row.is_hazardous || false,
                    hazardClass: row.hazard_class || undefined,
                    totalWeightKg: row.total_weight_kg || undefined,
                    totalVolumeCbm: row.total_volume_cbm || undefined,
                    totalPackages: row.total_packages || undefined,
                    cargoDescription: row.cargo_description || undefined,
                    hsCodePrimary: row.hs_code_primary || undefined,

                    // Financial
                    totalValueUsd: row.total_value_usd || undefined,
                    freightChargesUsd: row.freight_charges_usd || undefined,
                    insuranceValueUsd: row.insurance_value_usd || undefined,

                    // Temperature control
                    requiresTemperatureControl: row.requires_temperature_control || false,
                    temperatureSetpointCelsius: row.temperature_setpoint_celsius || undefined,

                    // Dates
                    bookingDate: row.booking_date ? new Date(row.booking_date) : undefined,
                    estimatedDepartureDate: row.estimated_departure_date ? new Date(row.estimated_departure_date) : undefined,
                    actualDepartureDate: row.actual_departure_date ? new Date(row.actual_departure_date) : undefined,
                    actualArrivalDate: row.actual_arrival_date ? new Date(row.actual_arrival_date) : undefined,
                    estimatedDeliveryDate: row.estimated_delivery_date ? new Date(row.estimated_delivery_date) : undefined,

                    // Tracking
                    lastTrackedAt: parseUtcISO(row.last_tracked_at),
                    trackingProvider: row.tracking_provider || undefined,
                    originPortCode: row.origin_port_unlocode || undefined,
                    destinationPortCode: row.destination_port_unlocode || undefined,
                    alertCount: row.alert_count || 0,
                    hasActiveAlerts: row.has_active_alerts || false,

                    // Notes
                    specialInstructions: row.special_instructions || undefined,
                    customerNotes: row.customer_notes || undefined,

                    // Consignee
                    consigneeCompanyId: row.consignee_company_id || undefined,
                    consigneeName: row.consignee_name || undefined,

                    // Document count
                    documentCount: row.document_count || 0,

                    // Email count
                    emailCount: row.email_count || 0,

                    // Per-container status summaries
                    documentStatusOverall: row.document_status_overall || 'All docs are OK',
                    exogenStatusOverall: row.exogen_status_overall || 'All systems OK',

                    // Document issue details (from backend ContainerIssue counts)
                    docsWithIssues: row.docs_with_issues ?? 0,
                    docsPending: row.docs_pending ?? 0,
                    docStatusMessage: row.doc_status_message || row.document_status_overall,

                    // Last update tracking
                    lastUpdateAt: row.last_update_at ? new Date(row.last_update_at) : (row.updated_at ? new Date(row.updated_at) : undefined),
                    lastUpdateType: row.last_update_type || undefined,

                    // Date reference source
                    dateReferenceSource: row.date_reference_source as any,

                };
            });
    }, [tableData, searchTerm]);

    // Use API data when available
    useEffect(() => {
        if (apiContainers.length > 0) {
            setContainers(apiContainers);
        } else if (!tableLoading && tableData) {
            // API returned but no containers
            setContainers([]);
        }
    }, [apiContainers, tableLoading, tableData]);

    // Only show full-page loading on initial load (no data yet)
    // For subsequent fetches, keep showing current data (no flash)
    const isInitialLoad = !tableData && tableLoading;
    useEffect(() => {
        if (isInitialLoad) {
            setIsLoading(true);
        } else {
            setIsLoading(false);
        }
    }, [isInitialLoad]);

    // Helper per convertire color names a hex
    const getColorHex = (colorName: string): string => {
        switch (colorName) {
            case 'brand': return '#6938ef'; // brand-600
            case 'success': return '#079485'; // success-600
            case 'warning': return '#dc6803'; // warning-600
            case 'error': return '#d92d20'; // error-600
            default: return '#6b7280'; // gray-500
        }
    };

    // Calcola distribuzioni per i grafici - usa dati API se disponibili, altrimenti mock
    const distributionData = useMemo(() => {
        // Use API data if available
        if (statsData && statsData.total_containers > 0) {
            const total = statsData.total_containers;

            // Map API risk distribution to chart format
            const riskData = statsData.risk_distribution.map((item) => ({
                label: translateRiskLevel(item.label as RiskLevel),
                value: item.value,
                color: item.label === 'Low' ? getColorHex('success')
                    : item.label === 'Medium' ? getColorHex('warning')
                        : getColorHex('error'),
            }));

            // Map API shipment status distribution to chart format
            // API returns: Draft, Booked, In Transit, Arrived, Delivered, On Hold, Cancelled
            const getShipmentStatusColor = (label: string): string => {
                switch (label) {
                    case 'Draft':
                        return getColorHex('gray');
                    case 'Booked':
                    case 'In Transit':
                        return getColorHex('brand');
                    case 'Arrived':
                        return getColorHex('warning');
                    case 'Delivered':
                        return getColorHex('success');
                    case 'On Hold':
                        return getColorHex('warning');
                    case 'Cancelled':
                        return getColorHex('error');
                    default:
                        return getColorHex('gray');
                }
            };

            // Use the shipment status directly from API
            const statusData = statsData.status_distribution.map((item) => ({
                label: item.label,
                value: item.value,
                color: getShipmentStatusColor(item.label),
            }));

            // Map API document distribution to chart format
            const documentData = statsData.document_distribution.map((item) => ({
                label: translateDocumentStatus(item.label === 'Complete' ? 'Completo' : item.label as DocumentStatus),
                value: item.value,
                color: item.label === 'Complete' ? getColorHex('success')
                    : item.label === 'Pending' ? getColorHex('warning')
                        : getColorHex('error'),
            }));

            // Calculate document total separately (sum of documents, not container count)
            const documentTotal = statsData.document_distribution.reduce((sum, item) => sum + item.value, 0);

            return { riskData, statusData, documentData, total, documentTotal };
        }

        // Fallback to mock data calculation
        const total = containers.length;

        // Risk Levels
        const riskDistribution = containers.reduce((acc, c) => {
            acc[c.riskLevel] = (acc[c.riskLevel] || 0) + 1;
            return acc;
        }, {} as Record<RiskLevel, number>);

        const riskData = [
            { label: translateRiskLevel('Low'), value: riskDistribution.Low || 0, color: getColorHex('success') },
            { label: translateRiskLevel('Medium'), value: riskDistribution.Medium || 0, color: getColorHex('warning') },
            { label: translateRiskLevel('High'), value: riskDistribution.High || 0, color: getColorHex('error') },
            { label: translateRiskLevel('Critical'), value: riskDistribution.Critical || 0, color: getColorHex('error') },
        ];

        // Container Status - matches ShipmentStatus enum: Draft, Booked, In Transit, Arrived, Delivered, On Hold, Cancelled
        const statusDistribution = containers.reduce((acc, c) => {
            acc[c.status] = (acc[c.status] || 0) + 1;
            return acc;
        }, {} as Record<ContainerStatus, number>);

        const getShipmentStatusColor = (label: string): string => {
            switch (label) {
                case 'Draft': return getColorHex('gray');
                case 'Booked': return getColorHex('brand');
                case 'In Transit': return getColorHex('brand');
                case 'Arrived': return getColorHex('warning');
                case 'Delivered': return getColorHex('success');
                case 'On Hold': return getColorHex('warning');
                case 'Cancelled': return getColorHex('error');
                default: return getColorHex('gray');
            }
        };

        const statusData = (['Draft', 'Booked', 'In Transit', 'Arrived', 'Delivered', 'On Hold', 'Cancelled'] as ContainerStatus[]).map(s => ({
            label: translateContainerStatus(s),
            value: statusDistribution[s] || 0,
            color: getShipmentStatusColor(s),
        }));

        // Document Status
        const documentDistribution = containers.reduce((acc, c) => {
            acc[c.documentStatus] = (acc[c.documentStatus] || 0) + 1;
            return acc;
        }, {} as Record<DocumentStatus, number>);

        const documentData = [
            { label: translateDocumentStatus('Completo'), value: documentDistribution.Completo || 0, color: getColorHex('success') },
            { label: translateDocumentStatus('Pending'), value: documentDistribution.Pending || 0, color: getColorHex('warning') },
            { label: translateDocumentStatus('Error'), value: documentDistribution.Error || 0, color: getColorHex('error') },
        ];

        // Fallback documentTotal is same as container total for mock data
        const documentTotal = total;

        return { riskData, statusData, documentData, total, documentTotal };
    }, [containers, statsData, translateRiskLevel, translateContainerStatus, translateDocumentStatus]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('list.loading')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <div className="flex items-center gap-3">
                        <h1 className="text-2xl font-semibold text-primary">{t('list.title')}</h1>
                        <span className="text-lg font-medium text-tertiary">({totalCount})</span>
                    </div>
                    <p className="text-tertiary mt-1">
                        {t('list.subtitle')}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <ShipperOnboardingHeaderAction />
                    <div className="flex items-center gap-2 rounded-lg bg-secondary p-1 ring-1 ring-secondary_alt">
                        <Button
                            size="sm"
                            color={viewMode === 'recap' ? 'primary' : 'secondary'}
                            onClick={() => setViewMode('recap')}
                            iconLeading={LayoutGrid01}
                        >
                            Recap
                        </Button>
                        <Button
                            size="sm"
                            color={viewMode === 'spreadsheet' ? 'primary' : 'secondary'}
                            onClick={() => setViewMode('spreadsheet')}
                            iconLeading={Grid01}
                        >
                            Spreadsheet
                        </Button>
                        {/* <Button
                            size="sm"
                            color="secondary"
                            onClick={() => navigate('/shipper/live-tracking', { state: navigationStateFrom(location) })}
                            iconLeading={Globe02}
                        >
                            {t('list.viewMap')}
                        </Button> */}
                    </div>
                    <Link
                        to="/shipper/containers/new"
                        state={navigationStateFrom(location)}
                        className="inline-flex items-center justify-center gap-1 rounded-lg bg-brand-solid px-3 py-2 text-sm font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                    >
                        <Plus className="size-5" />
                        {t('list.newContainer')}
                    </Link>
                </div>
            </div>

            {/* Filters / Sorting toggles */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                    <Button
                        size="sm"
                        color="secondary"
                        onClick={() => setShowFiltersPanel((prev) => !prev)}
                        iconLeading={SearchLg}
                    >
                        {showFiltersPanel
                            ? t('list.filters.hideFilters', { defaultValue: 'Hide filters' })
                            : t('list.filters.showFilters', { defaultValue: 'Search filters' })}
                    </Button>
                </div>
                {viewMode === 'recap' && (
                    <Button
                        size="sm"
                        color="secondary"
                        onClick={() => setShowRecapSidebar((prev) => !prev)}
                        iconTrailing={LayoutRight}
                    >
                        {showRecapSidebar
                            ? t('list.recap.hideSidebar', { defaultValue: 'Hide stats' })
                            : t('list.recap.showSidebar', { defaultValue: 'Show stats' })}
                    </Button>
                )}
            </div>

            {/* Collapsible search filters */}
            {showFiltersPanel && (
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-12 lg:grid-rows-2">
                        {/* Col 1 (span 3), Row 1: Date picker */}
                        <div className="lg:col-span-3 [&_button]:w-full">
                            <DateRangePicker
                                value={dateRange}
                                onChange={setDateRange}
                            />
                        </div>
                        {/* Col 2 (span 5), Row 1+2: Status (spans 2 rows) */}
                        <div className="lg:col-span-5 lg:row-span-2">
                            <MultiSelect
                                placeholder={t('list.filters.statusPlaceholder', { defaultValue: 'Status' })}
                                items={statusOptions}
                                selectedItems={selectedStatusFilters}
                                placeholderIcon={null}
                                size="md"
                            >
                                {(item) => (
                                    <MultiSelect.Item key={item.id} id={item.id} textValue={item.label}>
                                        {item.label}
                                    </MultiSelect.Item>
                                )}
                            </MultiSelect>
                        </div>
                        {/* Col 3 (span 2-3), Row 1: Risk level */}
                        <div className="lg:col-span-2">
                            <MultiSelect
                                placeholder={t('list.filters.riskLevelPlaceholder', { defaultValue: 'Risk Level' })}
                                items={riskLevelOptions}
                                selectedItems={selectedRiskLevels}
                                placeholderIcon={null}
                            >
                                {(item) => (
                                    <MultiSelect.Item key={item.id} id={item.id} textValue={item.label}>
                                        {item.label}
                                    </MultiSelect.Item>
                                )}
                            </MultiSelect>
                        </div>
                        {/* Col 4 (span 2), Row 1: Carriers */}
                        <div className="lg:col-span-2">
                        <Select.ComboBox
                            placeholder={t('list.filters.carrierPlaceholder', { defaultValue: 'Carrier' })}
                            items={carrierOptions}
                            selectedKey={carrierNameFilter}
                            onSelectionChange={(key) => setCarrierNameFilter((key as string) ?? '')}
                            shortcut={false}
                            size="md"
                        >
                            {(item) => (
                                <Select.Item id={item.id} textValue={item.label}>
                                    {item.label}
                                </Select.Item>
                            )}
                        </Select.ComboBox>
                        </div>
                        {/* Col 1 (span 3), Row 2: Search */}
                        <div className="lg:col-span-3">
                            <Input
                                placeholder={t('list.filters.searchPlaceholder', { defaultValue: 'Search...' })}
                                value={searchTerm}
                                onChange={setSearchTerm}
                                icon={SearchLg}
                                size="md"
                            />
                        </div>
                        {/* Col 3-4 (span 4), Row 2: Toggle flags — compact row aligned with Risk Level + Carriers */}
                        <div className="lg:col-span-4 flex items-center gap-4 px-3 py-2 rounded-lg bg-secondary ring-1 ring-secondary_alt">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                                <Toggle
                                    isSelected={hasDocIssuesFilter}
                                    onChange={(selected) => setHasDocIssuesFilter(selected)}
                                    aria-label={t('list.filters.docIssuesLabel', { defaultValue: 'Doc issues' })}
                                />
                                <span className="text-xs text-primary whitespace-nowrap">
                                    {t('list.filters.docIssuesLabel', { defaultValue: 'Doc issues' })}
                                </span>
                            </label>
                            <div className="w-px h-4 bg-tertiary/30" />
                            <label className="flex items-center gap-1.5 cursor-pointer">
                                <Toggle
                                    isSelected={hasActiveAlertsFilter}
                                    onChange={(selected) => setHasActiveAlertsFilter(selected)}
                                    aria-label={t('list.filters.activeAlertsLabel', { defaultValue: 'Alerts' })}
                                />
                                <span className="text-xs text-primary whitespace-nowrap">
                                    {t('list.filters.activeAlertsLabel', { defaultValue: 'Alerts' })}
                                </span>
                            </label>
                            <div className="w-px h-4 bg-tertiary/30" />
                            <label className="flex items-center gap-1.5 cursor-pointer">
                                <Toggle
                                    isSelected={hasPartialDataFilter}
                                    onChange={(selected) => setHasPartialDataFilter(selected)}
                                    aria-label={t('list.filters.partialDataLabel', { defaultValue: 'Partial data' })}
                                />
                                <span className="text-xs text-primary whitespace-nowrap">
                                    {t('list.filters.partialDataLabel', { defaultValue: 'Partial data' })}
                                </span>
                            </label>
                        </div>
                    </div>
                </div>
            )}

            {/* Containers View */}
            <div key={viewMode}>
                {viewMode === 'recap' ? (
                    <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
                        <div className={`${showRecapSidebar ? 'xl:col-span-9' : 'xl:col-span-12'} space-y-4`}>
                            <RecentContainersTable
                                containers={displayedContainers}
                                maxCount={pageSize}
                                searchParams={searchParams}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSortChange={handleSortChange}
                            />
                            {totalCount > 0 && (
                                <div className="flex items-center justify-between py-4 px-6 bg-primary rounded-lg ring-1 ring-secondary">
                                    <div className="flex items-center gap-3">
                                        <div className="text-sm text-tertiary">
                                            {t('list.pagination.showing', {
                                                from: (currentPage - 1) * pageSize + 1,
                                                to: Math.min(currentPage * pageSize, totalCount),
                                                total: totalCount,
                                                defaultValue: `Showing ${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, totalCount)} of ${totalCount}`
                                            })}
                                        </div>
                                        <Select
                                            placeholder={t('list.pagination.itemsPerPage', { defaultValue: 'Items per page' })}
                                            items={[
                                                { id: '10', label: '10' },
                                                { id: '20', label: '20' },
                                                { id: '30', label: '30' },
                                                { id: '50', label: '50' },
                                                { id: '100', label: '100' },
                                            ]}
                                            selectedKey={String(pageSize)}
                                            onSelectionChange={(key) => {
                                                const next = Number(key);
                                                if (![10, 20, 30, 50, 100].includes(next)) return;
                                                setPageSize(next);
                                                setCurrentPage(1);
                                            }}
                                            size="sm"
                                        >
                                            {(item) => (
                                                <Select.Item id={item.id} textValue={item.label}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            size="sm"
                                            color="secondary"
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            isDisabled={currentPage === 1}
                                        >
                                            {t('list.pagination.previous', { defaultValue: 'Previous' })}
                                        </Button>
                                        <span className="text-sm text-primary px-3">
                                            {t('list.pagination.page', { current: currentPage, total: totalPages, defaultValue: `${currentPage} / ${totalPages}` })}
                                        </span>
                                        <Button
                                            size="sm"
                                            color="secondary"
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                            isDisabled={currentPage >= totalPages}
                                        >
                                            {t('list.pagination.next', { defaultValue: 'Next' })}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                        {showRecapSidebar && (
                            <aside className="xl:col-span-3">
                                <DistributionChartsSection
                                    distributionData={distributionData}
                                    statsData={statsData}
                                    startDate={startDate}
                                    endDate={endDate}
                                    layout="stack"
                                />
                            </aside>
                        )}
                    </div>
                ) : viewMode === 'spreadsheet' ? (
                    <ContainerSpreadsheetView
                        containers={displayedContainers}
                        totalCount={totalCount}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSortChange={handleSortChange}
                        onUpdate={(_containerId, _field, _value) => {
                            // Refresh data from API after update
                            refetchAllData();
                        }}
                        onContainerClick={(containerId) => {
                            const currentView = searchParams.get('view');
                            const url = currentView
                                ? `/shipper/containers/${containerId}?view=${currentView}`
                                : `/shipper/containers/${containerId}`;
                            navigate(url, { state: navigationStateFrom(location) });
                        }}
                    />
                ) : null}

                {/* Pagination Controls */}
                {viewMode === 'spreadsheet' && totalCount > 0 && (
                    <div className="flex items-center justify-between py-4 px-6 bg-primary rounded-lg ring-1 ring-secondary">
                        <div className="flex items-center gap-3">
                            <div className="text-sm text-tertiary">
                                {t('list.pagination.showing', {
                                    from: (currentPage - 1) * pageSize + 1,
                                    to: Math.min(currentPage * pageSize, totalCount),
                                    total: totalCount,
                                    defaultValue: `Showing ${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, totalCount)} of ${totalCount}`
                                })}
                            </div>
                            <Select
                                placeholder={t('list.pagination.itemsPerPage', { defaultValue: 'Items per page' })}
                                items={[
                                    { id: '10', label: '10' },
                                    { id: '20', label: '20' },
                                    { id: '30', label: '30' },
                                    { id: '50', label: '50' },
                                    { id: '100', label: '100' },
                                ]}
                                selectedKey={String(pageSize)}
                                onSelectionChange={(key) => {
                                    const next = Number(key);
                                    if (![10, 20, 30, 50, 100].includes(next)) return;
                                    setPageSize(next);
                                    setCurrentPage(1);
                                }}
                                size="sm"
                            >
                                {(item) => (
                                    <Select.Item id={item.id} textValue={item.label}>
                                        {item.label}
                                    </Select.Item>
                                )}
                            </Select>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                size="sm"
                                color="secondary"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                isDisabled={currentPage === 1}
                            >
                                {t('list.pagination.previous', { defaultValue: 'Previous' })}
                            </Button>
                            <span className="text-sm text-primary px-3">
                                {t('list.pagination.page', { current: currentPage, total: totalPages, defaultValue: `${currentPage} / ${totalPages}` })}
                            </span>
                            <Button
                                size="sm"
                                color="secondary"
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                isDisabled={currentPage >= totalPages}
                            >
                                {t('list.pagination.next', { defaultValue: 'Next' })}
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {containers.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <Package className="size-12 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-primary mb-2">{t('list.table.noContainers.title')}</h3>
                        <p className="text-tertiary">
                            {searchTerm || hasNonDefaultStatusFilter || riskLevelFilters.length > 0 || hasDocIssuesFilter || hasActiveAlertsFilter || carrierNameFilter
                                ? t('list.table.noContainers.message')
                                : t('list.table.noContainers.emptyMessage')
                            }
                        </p>
                    </div>
                    {!searchTerm && !hasNonDefaultStatusFilter && riskLevelFilters.length === 0 && !hasDocIssuesFilter && !carrierNameFilter && (
                        <Link
                            to="/shipper/containers/new"
                            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                        >
                            <Plus className="size-5" />
                            {t('list.table.noContainers.createFirst')}
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
};
